import React, {
  useImperativeHandle,
  useRef,
  useState,
  ComponentRef,
  forwardRef,
} from 'react';

import { IConfirmAbsenceWarningDTO } from 'shared/domain/dtos';
import { TAbsenceWarningChannel } from 'shared/domain/entities';
import {
  makeConfirmAbsenceWarningUseCase,
  makeEvaluateAbsenceWarningUseCase,
} from 'shared/domain/useCases';
import {
  Modal,
  ControlledCheckInput,
} from 'shared/presentation/components/atoms';
import { Button } from 'shared/presentation/components/molecules';
import { useLocale, useToast } from 'shared/presentation/contexts';

import * as S from './styles';

interface IAbsenceWarningModalRef {
  evaluate(params: {
    customerId: number;
    scheduleId?: number;
    channel: TAbsenceWarningChannel;
  }): Promise<boolean>;
}

const AbsenceWarningModal: React.ForwardRefRenderFunction<
  IAbsenceWarningModalRef,
  unknown
> = (_props, ref) => {
  const modalRef = useRef<ComponentRef<typeof Modal>>(null);
  const resolveRef = useRef<((value: boolean) => void) | null>(null);
  const confirmationParamsRef = useRef<IConfirmAbsenceWarningDTO | null>(null);

  const { t } = useLocale('shared');
  const toast = useToast();

  const [message, setMessage] = useState<string | null>(null);
  const [checked, setChecked] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const settleEvaluation = (value: boolean) => {
    resolveRef.current?.(value);
    resolveRef.current = null;
  };

  useImperativeHandle(
    ref,
    () => ({
      evaluate: async ({ customerId, scheduleId, channel }) => {
        try {
          const warning = await makeEvaluateAbsenceWarningUseCase().execute({
            customerId,
            scheduleId,
          });

          if (!warning.shouldShow || !warning.moment) return true;

          confirmationParamsRef.current = {
            customerId,
            moment: warning.moment,
            channel,
          };
          setMessage(warning.message);
          setChecked(false);

          return await new Promise<boolean>(resolve => {
            resolveRef.current = resolve;
            modalRef.current?.open();
          });
        } catch {
          toast.error(
            t('There was an error loading this warning. Please try again.'),
          );

          // Fail-open de propósito: o backend revalida a confirmação pendente
          // antes de persistir agendamento/reagendamento/cancelamento (canal SITE),
          // então um erro aqui é só risco de UX (ação tentada e barrada com erro
          // genérico), não de regra de negócio.
          return true;
        }
      },
    }),
    [t, toast],
  );

  const handleConfirm = async () => {
    if (!confirmationParamsRef.current) return;

    setConfirming(true);

    try {
      await makeConfirmAbsenceWarningUseCase().execute(
        confirmationParamsRef.current,
      );

      settleEvaluation(true);
      modalRef.current?.close();
    } catch {
      toast.error(
        t('There was an error confirming this warning. Please try again.'),
      );
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Modal
      ref={modalRef}
      closeOnOverlayClick={false}
      onClose={() => settleEvaluation(false)}
    >
      <S.Container>
        <h2>{t('Absence and cancellation warning')}</h2>

        <p>{message}</p>

        <ControlledCheckInput
          id="absence-warning-checkbox"
          value={checked}
          onChange={setChecked}
        >
          {t("I've read and understood")}
        </ControlledCheckInput>

        <Button
          variant="primary"
          type="button"
          disabled={!checked}
          loading={confirming}
          onClick={handleConfirm}
        >
          {t('Confirm')}
        </Button>
      </S.Container>
    </Modal>
  );
};

export default forwardRef(AbsenceWarningModal);
